// Order whales (large limit orders) list widget. Body = list, header
// (OrdersHeader named export) holds sort/filter/coin.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSharedOrderWhales } from '../data/sharedOrderWhales';
import { sortOrderWhales } from '../../../hooks/useSortModes';
import { useWidgetSetting } from '../cockpitContext.jsx';
import {
  VirtualList, OrderRow, FilterDropdown, SortToggle, FILTER_OPTIONS,
} from '../../CoinTerminalSections/sectionHelpers';
import CoinPickerHeader from './CoinPickerHeader';

function OrdersWidgetInstance({ instanceId, defaultCoin }) {
  const { t } = useTranslation();
  const [coin] = useWidgetSetting(instanceId, 'coin', defaultCoin);
  const [orderFilter] = useWidgetSetting(instanceId, 'filter', 0);
  const [sortMode] = useWidgetSetting(instanceId, 'sort', 'size');
  const { levels: orderWhalesLevels } = useSharedOrderWhales(coin);

  const filtered = useMemo(() => {
    const levels = orderWhalesLevels || [];
    const f = orderFilter > 0 ? levels.filter((l) => l.size >= orderFilter) : levels;
    return sortOrderWhales(f, sortMode);
  }, [orderWhalesLevels, orderFilter, sortMode]);

  const maxSize = filtered.length > 0 ? Math.max(...filtered.map((o) => o.size)) : 1;

  if (filtered.length === 0) {
    return <div className="ct-section-content"><div className="ct-empty">{t('chartPage.empty.orders', 'No orders matching filter')}</div></div>;
  }
  return (
    <VirtualList
      items={filtered}
      rowHeight={32}
      getKey={(o) => `${o.side}:${o.price}`}
      renderRow={(order) => <OrderRow order={order} barWidth={(order.size / maxSize) * 60} />}
    />
  );
}

export const OrdersHeader = React.memo(function OrdersHeader({ instanceId, defaultCoin }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useWidgetSetting(instanceId, 'filter', 0);
  const [sortMode, setSortMode] = useWidgetSetting(instanceId, 'sort', 'size');
  const toggleSort = () => setSortMode((m) => (m === 'time' ? 'size' : 'time'));
  return (
    <>
      <SortToggle
        mode={sortMode}
        onToggle={toggleSort}
        labelTime={t('chartPage.sortByTime', 'Sort by time')}
        labelSize={t('chartPage.sortBySize', 'Sort by size')}
      />
      <FilterDropdown options={FILTER_OPTIONS.order} value={filter} onChange={setFilter} />
      <CoinPickerHeader instanceId={instanceId} defaultCoin={defaultCoin} />
    </>
  );
});

export default React.memo(OrdersWidgetInstance);
